type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TurnstileControl = {
  getToken: () => string;
  reset: () => void;
};

let siteKeyPromise: Promise<string> | null = null;
let scriptPromise: Promise<void> | null = null;

async function getSiteKey(): Promise<string> {
  if (!siteKeyPromise) {
    siteKeyPromise = fetch('/api/public-config')
      .then((response) => response.json())
      .then((body: unknown) => {
        const envelope = body as { data?: { turnstileSiteKey?: string } };
        return envelope.data?.turnstileSiteKey || '';
      });
  }
  return siteKeyPromise;
}

async function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return;
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Security widget failed to load.'));
      document.head.appendChild(script);
    });
  }
  await scriptPromise;
}

export async function mountTurnstile(container: HTMLElement, action: string): Promise<TurnstileControl> {
  const siteKey = await getSiteKey();
  if (!siteKey) {
    container.hidden = true;
    return { getToken: () => '', reset: () => undefined };
  }

  await loadTurnstileScript();
  let token = '';
  const widgetId = window.turnstile?.render(container, {
    sitekey: siteKey,
    action,
    callback: (value: string) => { token = value; },
    'expired-callback': () => { token = ''; },
    'error-callback': () => { token = ''; },
  });

  return {
    getToken: () => token,
    reset: () => {
      token = '';
      if (window.turnstile && widgetId) window.turnstile.reset(widgetId);
    },
  };
}
